/**
 * OVERVOLT live-run persistence.
 *
 * The persistent **profile** (Flux, unlock tree, Scoreboard — see
 * `game/profile.ts`) is between-run meta state. This module saves the *active*
 * run — the placed towers, current wave, gold, lives, Overload tier, ability
 * cooldowns, RNG cursor, every transient field — into a sibling localStorage
 * blob so that a browser reload mid-run does not vaporise the player's
 * progress. The two saves are independent: the profile lives across many runs;
 * the run save is the single in-progress run and is wiped when it ends.
 *
 * Determinism: the saved blob carries the RNG cursor alongside the seed, so a
 * resumed run reproduces the *same* future sequence — not just the same seed.
 *
 * Save boundaries (see `engine/Game.ts`):
 *   - the wave-cleared → build phase transition (the cheapest deterministic
 *     quiet moment — no enemies on the field, no projectiles in flight);
 *   - the page `beforeunload` / `visibilitychange→hidden` events, so a tab
 *     close or a refresh during a wave still captures the run-in-progress.
 *
 * The save is **cleared** when the run ends (`won` / `lost`) and when the
 * player starts a fresh run (`reset`). A malformed or version-mismatched blob
 * is treated as no save — it is cleared on the next save attempt; the load
 * path never throws (mirrors `profile.ts`'s parse-safe contract).
 */
import type { Difficulty } from '../config';
import { CONFIG } from '../config';
import type { GameState, GameMode } from './state';

/** localStorage key for the single live-run blob (the default un-named profile). */
export const RUN_SAVE_KEY = 'overvolt.run';

/**
 * Resolve the `localStorage` key for the live-run blob. Default (un-named)
 * returns the legacy {@link RUN_SAVE_KEY} for backward compatibility; a named
 * profile (Sprint 54 — `?profile=<name>`) appends a dot-separated suffix:
 * `overvolt.run.alice`. The caller is responsible for sanitising `name`.
 */
export function runKeyFor(name?: string): string {
  return name ? `${RUN_SAVE_KEY}.${name}` : RUN_SAVE_KEY;
}

/**
 * Run-save schema version. Bump on any change to {@link SavedRun}'s shape;
 * a stored blob with a mismatched version is discarded (the player loses the
 * resume but never gets a crash on a stale shape).
 *
 * - **v1** — Sprint 16 baseline.
 * - **v2** — Sprint 20 (abilities-s20b): adds `state.aftershockTicksLeft`
 *   and `state.scorchZones` for the proactive Airstrike + Overcharge re-role.
 * - **v3** — Sprint 26 (REC 3, overclock-impl-s33): adds `Tower.overclock`
 *   and `PowerState.overclockCount`. An old v2 save would deserialise with
 *   `overclock === undefined` (falsy — sim functionally fine) and
 *   `overclockCount === undefined` until the next tick; the version bump
 *   simply invalidates rather than carrying the silent partial state.
 * - **v4** — Sprint 39 (REC 3.b, pylon-aura-s39): adds the derived
 *   `Tower.auraRateMult` cache (docs/REC3_DESIGN.md §2.B). An old v3 save
 *   would deserialise with `auraRateMult === undefined`, which would turn
 *   the `applyCooldown` divisor into NaN and stall every weapon tower's
 *   fire loop. The bump invalidates instead — and `Game.applyResume` calls
 *   `applyPylonAuras` after rehydration anyway so a forward-migrated save
 *   would also rebuild the cache, but the version bump is the explicit
 *   gate (the same discipline Sprint 26 used).
 */
export const RUN_SAVE_VERSION = 4;

/** The persisted blob — the full live state plus the RNG cursor. */
export interface SavedRun {
  /** Schema version — see {@link RUN_SAVE_VERSION}. */
  runSchemaVersion: number;
  /** The full GameState. JSON-clean: every field is a plain value or array. */
  state: GameState;
  /** Opaque PRNG cursor captured via `Rng.getState()`. */
  rngState: number;
}

/**
 * True when this run is worth saving — there is real player progress on the
 * board. We deliberately skip the fresh title screen (build, wave 0, no towers)
 * and the run-end (won/lost) screens; the run-end clear handles those.
 */
export function shouldPersist(state: GameState): boolean {
  if (state.phase === 'won' || state.phase === 'lost') return false;
  if (state.phase === 'build' && state.wave === 0 && state.towers.length === 0) return false;
  return true;
}

/**
 * Serialise + persist `state` (with the RNG cursor) under {@link RUN_SAVE_KEY}.
 * Failures are non-fatal — a full / blocked / unavailable `localStorage` must
 * not crash the live run. Mirrors `profile.ts`'s `saveProfile` contract. Pass
 * `name` to write to a named profile's run slot (Sprint 54); omitting it
 * targets the legacy default key for backward compatibility.
 */
export function persistRun(state: GameState, rngState: number, name?: string): void {
  const blob: SavedRun = {
    runSchemaVersion: RUN_SAVE_VERSION,
    state,
    rngState: rngState >>> 0,
  };
  try {
    localStorage.setItem(runKeyFor(name), JSON.stringify(blob));
  } catch {
    // Ignore — saving is a best-effort convenience.
  }
}

/** Drop the live-run blob. Best-effort — never throws. */
export function clearSavedRun(name?: string): void {
  try {
    localStorage.removeItem(runKeyFor(name));
  } catch {
    // Ignore.
  }
}

/** Has-saved-run probe — true iff a parseable, current-version blob is present. */
export function hasSavedRun(name?: string): boolean {
  return loadSavedRun(name) !== null;
}

/**
 * Try to load the saved run. Returns the {@link SavedRun} on success, or `null`
 * for any of: no save present, parse failure, schema-version mismatch, or
 * structural validation failure. Never throws — a corrupt blob must not block
 * the game from booting (the caller can {@link clearSavedRun} as a follow-up).
 */
export function loadSavedRun(name?: string): SavedRun | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(runKeyFor(name));
  } catch {
    return null;
  }
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  return coerceSavedRun(parsed);
}

/**
 * Validate a parsed blob into a {@link SavedRun}, or `null` if it cannot be
 * trusted. A version mismatch is rejected outright (unlike the profile, the
 * run save is recreatable from the next wave-clear, so a forward-migration
 * dance is not worth the risk of a partially-coerced live state).
 */
function coerceSavedRun(raw: unknown): SavedRun | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  if (r.runSchemaVersion !== RUN_SAVE_VERSION) return null;
  if (typeof r.rngState !== 'number' || !Number.isFinite(r.rngState)) return null;
  const state = coerceState(r.state);
  if (!state) return null;
  return {
    runSchemaVersion: RUN_SAVE_VERSION,
    state,
    rngState: (r.rngState as number) >>> 0,
  };
}

/** Minimal structural validation — the core fields a resume cannot work without. */
function coerceState(raw: unknown): GameState | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const s = raw as Record<string, unknown>;
  // Discriminated phase / mode / difficulty — the union members the engine
  // case-analyses on. A blob with anything else is treated as corrupt.
  if (s.phase !== 'build' && s.phase !== 'wave' && s.phase !== 'won' && s.phase !== 'lost') {
    return null;
  }
  if (s.mode !== 'campaign' && s.mode !== 'endless' && s.mode !== 'maze') return null;
  if (s.difficulty !== 'easy' && s.difficulty !== 'normal' && s.difficulty !== 'hard') return null;
  if (typeof s.seed !== 'number' || !Number.isFinite(s.seed)) return null;
  if (typeof s.wave !== 'number' || !Number.isFinite(s.wave)) return null;
  if (!Array.isArray(s.towers) || !Array.isArray(s.enemies) || !Array.isArray(s.path)) return null;
  // Trust the rest — they were written by our own serialiser; the schema
  // version guard above is the change-detector. Cast through unknown so the
  // GameState shape is preserved without re-validating every leaf.
  return raw as GameState;
}

// --- rehydration -------------------------------------------------------------

/**
 * Post-deserialisation fix-up: walk every alive enemy on a fixed-path map and
 * re-point its `path` at the singleton `state.path`. Fixed-path mode shares one
 * `PATH` instance across every enemy, but JSON round-trip turns that into many
 * independent arrays. Maze Mode enemies keep their own independent A* routes,
 * so this is a no-op there. Pure structural touch-up — no sim mutation.
 *
 * The caller is expected to follow up with `recomputePaths(state)`, which
 * restores the canonical fixed-path singleton (or rebuilds the Maze A* route).
 */
export function relinkSharedPaths(state: GameState): void {
  if (state.mode === 'maze') return;
  for (const enemy of state.enemies) {
    enemy.path = state.path;
  }
}

/**
 * Resolve the right localStorage backend at runtime — `globalThis.localStorage`
 * in the browser. Kept here as a single seam so tests / non-browser callers can
 * shim it if ever needed; today the browser is the only host. Unused for now,
 * exported only so the surface stays honest.
 */
export function runSaveKey(): string {
  // Reference CONFIG to keep an eye on cross-cutting changes (profile keys live
  // there); the run key is intentionally not in config — it is owned here, near
  // the schema-version constant — so a future schema bump is one-place.
  void CONFIG.profileKey;
  return RUN_SAVE_KEY;
}

// Re-export type aliases so call sites need only import from this module.
export type { GameMode, Difficulty };
